import PropTypes from "prop-types";
import React, { useContext, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

export default function TenderInQuotation({
  tenderTypeId,
  mode,
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);

  const removeContextItems = useMemo(
    () => ["Print", "Copy", "Create", "Edit", "Delete"],
    []
  );

  //============================================================================================================
  // Function to handle the creation of a new route based on the operation and data
  //============================================================================================================
  const NewRoute = useCallback(
    (operation, data) => {
      if (!data) {
        toastMessages("error", "", t("SelectedLine")); // Show error if no data is selected
        return;
      }
      dispatch(
        M_StateActions.addM_State({ key: "operation", value: operation })
      ); // Store the operation in the state
      dispatch(M_StateActions.addM_State({ key: "TenderLine", value: data })); // Store the selected tender line in the state

      const route = "tenders/TenderInQuotation"; // Route to the TenderInQuotation page

      const label = `${t("LabelInQuotationTender")} "${
        data?.RequestForQuotes?.Name || data?.RequestForQuotes || "Tender"
      }"`;
      onCreateTab(route, label);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  //============================================================================================================
  // replaceFunctions:
  // - Memoized map to keep a stable reference for MTable.
  // - Prevents downstream effects and unnecessary refetches caused by new function identities on each render.
  // - Exposes only the allowed operation(s) for the "in quotation" context.
  //============================================================================================================
  const replaceFunctions = useMemo(
    () => new Map([["View", (e) => NewRoute("View", e)]]),
    [NewRoute]
  );

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          <div style={{ color: ctx.secondaryColor }}>
            {mode === "spot"
              ? t("SpotTendersInQuotation")
              : t("NonSpotTendersInQuotation")}
          </div>
          <div className="labelSubtitleSettings">
            {mode === "spot"
              ? t("InfoSpotTendersInQuotation")
              : t("InfoNonSpotTendersInQuotation")}
          </div>
        </div>
      </div>

      {/* Main dynamic Table for Tenders, that are not Finished yet */}
      <MTable
        filtersOnTable
        entityName={"Tenders"} // Fetch for Tenders Table data
        roleName={"RequestForQuotes"} // Permissions based on RequestForQuotes roles
        rolePermissions={["Read", "View"]} // Only allow Read and View permissions
        removeContextItems={removeContextItems} // Remove unnecessary context menu items
        refresh={refresh}
        setRefresh={setRefresh}
        useSessionStorage={false}
        FILTER={`TenderTypeId eq ${tenderTypeId} and IsFinished eq false`} // Filter by the specific Tender Type ID (Spot or Non Spot depending on the Page) that are not finished yet
        replaceFunctions={replaceFunctions} // Only allow the View operation
        tabsRailWidth={250}
      />
    </div>
  );
}

TenderInQuotation.propTypes = {
  tenderTypeId: PropTypes.number.isRequired, // 1 = Spot, 2 = NonSpot
  mode: PropTypes.oneOf(["spot", "nonspot"]), // user mode for the tender type if needed
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};
